import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@finitless/shared/lib";
import { useApiStore } from "@finitless/shared/stores";
import { useToastContext } from "@finitless/shared/contexts";

/**
 * Build minimal headers for plugin API calls to avoid CORS issues
 * (external plugin servers don't allow X-INTERNAL-PRODUCT-X, X-APP-X, etc.)
 */
const getPluginHeaders = () => {
  const headers = { "Content-Type": "application/json" };
  const token = apiClient.getAuthToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
};

/**
 * Hook to manage plugin environment (sandbox/production) toggle
 * @param {string} pluginRoot - The plugin root URL (e.g. https://clover.plugins.orderingplus.com/)
 * @returns {Object} Environment query and toggle mutation
 */
export const usePluginEnvironment = (pluginRoot) => {
  const projectCode = useApiStore((state) => state.project);
  const queryClient = useQueryClient();
  const { showToast } = useToastContext();

  const queryKey = ["pluginEnvironment", pluginRoot, projectCode];

  const environmentQuery = useQuery({
    queryKey,
    queryFn: async () => {
      if (!projectCode || !pluginRoot) return null;
      const baseUrl = pluginRoot.replace(/\/$/, "");
      const response = await fetch(
        `${baseUrl}/${projectCode}/api/environment`,
        { headers: getPluginHeaders() },
      );
      const data = await response.json();
      return data?.result || data;
    },
    enabled: !!projectCode && !!pluginRoot,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ environment }) => {
      const baseUrl = pluginRoot.replace(/\/$/, "");
      const response = await fetch(
        `${baseUrl}/${projectCode}/api/environment`,
        {
          method: "POST",
          headers: getPluginHeaders(),
          body: JSON.stringify({ environment }),
        },
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.message || "Failed to update environment");
      }
      return data?.result || data;
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(queryKey, data);
      showToast(
        variables.environment === "sandbox"
          ? "Sandbox mode enabled"
          : "Production mode enabled",
        "success",
      );
    },
    onError: (error) => {
      showToast(error.message || "Failed to update environment", "error");
    },
  });

  const isSandbox =
    environmentQuery.data?.environment === "sandbox" ||
    environmentQuery.data === "sandbox";

  const toggleEnvironment = () => {
    toggleMutation.mutate({
      environment: isSandbox ? "production" : "sandbox",
    });
  };

  return {
    environment: environmentQuery.data,
    isSandbox,
    isLoading: environmentQuery.isLoading,
    toggleEnvironment,
    isToggling: toggleMutation.isPending,
  };
};

export default usePluginEnvironment;
